import React, { Fragment, useState } from 'react'
import { Flex } from 'reflexbox'
import { Formik, Form, FieldArray } from 'formik'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as Yup from 'yup'
import moment from 'moment'

import Breadcrumb from 'components/Breadcrumb'
import Card from 'components/Card'
import { Title } from 'components/Typography'
import Button from 'components/Button'
import { SelectField, NumberField, SingleDatePickerField } from 'components/Form'
import Loader from 'components/Loader'
import { Close } from 'components/Icons'

import { getPartners } from 'services/partners'
import { getCardTypes, postCardOrder } from 'services/giftCards'

import { AddInputsButton, RemoveInputsButton, OrderInputsContainer } from './style'

const breadcrumbPath = [
  {
    label: 'Cartões presente',
    link: '/staff/prepaid-partners/gift-card',
  },
  {
    label: 'Novo pedido',
  },
]

const initialValues = {
  partner: '',
  order: [{ quantity: undefined, card_type: '', initial_date: undefined, expiration_date: undefined }],
  total: undefined,
  date: {
    startDate: undefined,
    endDate: undefined,
  },
}

const validationSchema = Yup.object().shape({
  partner: Yup.object().required(),
  order: Yup.array().of(
    Yup.object().shape({
      quantity: Yup.number().required(),
      card_type: Yup.string().required(),
      initial_date: Yup.mixed().required(),
      expiration_date: Yup.mixed().required(),
    }),
  ),
  total: Yup.string().required(),
})

const NewGiftCard = () => {
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 1,
    totalRecords: 0,
  })
  const [giftCardPagination, setGiftCardPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 1,
    totalRecords: 0,
  })

  const history = useHistory()

  const loadPartnerOptions = async search => {
    try {
      if (search) {
        setPagination({ page: 1, limit: 10 })
      }

      const res = await getPartners({ pagination, ...(search && { search }) })
      const formattedData = res.partners.map(partner => ({ label: partner.company_name, value: partner.id }))

      if (res.pagination.currentPage !== res.pagination.totalPages)
        setPagination({ ...pagination, currentPage: res.pagination.currentPage + 1, pageSize: res.pagination.pageSize })

      return {
        options: formattedData,
        hasMore: res.pagination.currentPage !== res.pagination.totalPages,
      }
    } catch (e) {
      console.log(e)
      toast.error('Aconteceu um erro ao carregar os dados de parceiro. Por favor tente novamente.')
    }
  }

  const loadGiftCardOptions = async search => {
    try {
      if (search) {
        setGiftCardPagination({ page: 1, limit: 10, ...giftCardPagination })
      }

      const res = await getCardTypes({ ...giftCardPagination, ...(search && { search }) })

      const formattedData = res.cards.map(card => ({ label: card.description, value: card }))

      if (res.pagination.currentPage !== res.pagination.totalPages)
        setGiftCardPagination({
          ...res.pagination,
          page: res.pagination.currentPage + 1,
          limit: res.pagination.pageSize,
        })

      return {
        options: formattedData,
        hasMore: search ? false : res.pagination.currentPage !== res.pagination.totalPages,
      }
    } catch (e) {
      console.log(e)
      toast.error('Aconteceu um erro ao carregar os dados dos cartões presente. Por favor tente novamente.')
    }
  }

  const handleSubmit = async values => {
    try {
      const formattedObject = {
        partner_id: values.partner.value,
        amount: Number(values.total),
        items: values.order.map(item => ({
          description: item.card_type.value?.description,
          category: item.card_type.value?.card_type,
          quantity: Number(item.quantity),
          initial_date: item.initial_date?.format('YYYY-MM-DD'),
          expiration_date: item.expiration_date?.format('YYYY-MM-DD'),
          [item.card_type.value?.card_type === 'GIFT_CARD' ? 'type_value_id' : 'type_plan_id']: item.card_type.value
            ?.origin_id,
          ...(item.card_type.value?.card_type === 'GIFT_CARD' && { unit_value: Number(item.card_type.value?.value) }),
        })),
      }

      await postCardOrder(formattedObject)
      toast.success('Pedido efetuado com sucesso!')
      history.push('/staff/prepaid-partners/gift-card')
    } catch (e) {
      console.log(e)
      toast.error('Aconteceu um erro ao fazer o pedido, por favor tente novamente.')
    }
  }
  return (
    <Flex flexDirection='column'>
      <Breadcrumb children={breadcrumbPath} />
      <Title as='h3'>Novo pedido</Title>

      <Card display='flex' flexDirection='column' mt={32}>
        <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
          {({ isSubmitting, values, errors }) => (
            <Flex as={Form} flexDirection='column' justifyContent='flex-start'>
              <Flex mb={32}>
                <SelectField
                  name='partner'
                  placeholder='Parceiro'
                  label='Parceiro'
                  errors={errors.partner}
                  loadOptions={loadPartnerOptions}
                  mr={24}
                  minWidth={384}
                />
                <NumberField name='total' placeholder='R$ 00,00' label='Valor total (R$)' prefix='R$ ' minWidth={384} />
              </Flex>

              <FieldArray
                name='order'
                render={arrayHelpers => (
                  <Fragment>
                    {values.order.map((_, index) => (
                      <OrderInputsContainer
                        alignItems='center'
                        justifyContent='flex-start'
                        maxWidth={792}
                        mb={values.order.length - 1 === index ? 24 : 32}
                      >
                        <NumberField
                          name={`order[${index}].quantity`}
                          placeholder='Quantidade'
                          label='Quantidade'
                          maxWidth={180}
                          mr={24}
                        />
                        <SelectField
                          name={`order[${index}].card_type`}
                          placeholder='Cartão'
                          label='Cartão'
                          minWidth={180}
                          loadOptions={loadGiftCardOptions}
                          mr={24}
                        />
                        <SingleDatePickerField
                          name={`order[${index}].initial_date`}
                          label='Válido de'
                          placeholder='dd/mm/aaaa'
                          mr={24}
                          maxWidth={180}
                        />
                        <SingleDatePickerField
                          name={`order[${index}].expiration_date`}
                          label='Válido até'
                          placeholder='dd/mm/aaaa'
                          mr={24}
                          maxWidth={180}
                          isOutsideRange={date => date.isBefore(moment(values?.order?.[index]?.initial_date))}
                          initialVisibleMonth={() => moment(values?.order?.[index]?.initial_date, 'month')}
                        />
                        {index !== 0 && (
                          <RemoveInputsButton type='button' onClick={() => arrayHelpers.remove(index)}>
                            <Close />
                          </RemoveInputsButton>
                        )}
                      </OrderInputsContainer>
                    ))}
                    <AddInputsButton
                      type='button'
                      onClick={() =>
                        arrayHelpers.push({
                          quantity: undefined,
                          card_type: '',
                          initial_date: undefined,
                          expiration_date: undefined,
                        })
                      }
                    >
                      Adicionar outro cartão
                    </AddInputsButton>
                  </Fragment>
                )}
              />
              <Flex justifyContent='flex-start'>
                <Button
                  type='button'
                  disabled={isSubmitting}
                  onClick={() => history.push('/staff/prepaid-partners/gift-card')}
                  outlined
                  mr={24}
                >
                  Cancelar
                </Button>
                <Button type='submit' disabled={isSubmitting}>
                  {isSubmitting ? <Loader /> : 'Salvar'}
                </Button>
              </Flex>
            </Flex>
          )}
        </Formik>
      </Card>
    </Flex>
  )
}

export default NewGiftCard
